import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CalendarDays, Clock, Repeat } from 'lucide-react';
import { formatEventDate } from '../lib/recurrence';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => formatEventDate(iso);

const VenueEventsListPage = () => {
  const { venueCode } = useParams();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/events/venue/${venueCode}/upcoming`);
        setEvents(r.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [venueCode]);

  return (
    <div className="min-h-screen bg-black text-white pb-12" data-testid="venue-events-list-page">
      <div className="max-w-xl mx-auto px-6 pt-8">
        <button
          onClick={() => navigate(`/v/${venueCode}`)}
          className="text-gray-400 hover:text-white text-sm flex items-center gap-1.5 mb-6"
          data-testid="venue-events-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <h1
          className="text-3xl lg:text-4xl font-extrabold text-white mb-2"
          style={{ fontFamily: 'Outfit' }}
        >
          Upcoming events
        </h1>
        <p className="text-gray-400 text-sm mb-8">
          Tap an event for details and to register.
        </p>

        {loading ? (
          <p className="text-gray-500">Loading…</p>
        ) : events.length === 0 ? (
          <div className="bg-white/[0.03] border border-white/10 rounded-3xl p-12 text-center">
            <CalendarDays className="w-12 h-12 text-gray-700 mx-auto mb-4" />
            <p className="text-white font-bold text-lg mb-1">No upcoming events</p>
            <p className="text-gray-500 text-sm">Check back soon — new events drop here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {events.map((ev) => (
              <button
                key={ev.id}
                onClick={() => navigate(`/event/${ev.share_code}`)}
                className="w-full bg-white/[0.03] border border-white/10 hover:border-[#FF007F]/50 rounded-2xl p-4 text-left transition-colors flex gap-4 items-stretch"
                data-testid={`venue-event-card-${ev.id}`}
              >
                {ev.poster_url ? (
                  <img
                    src={ev.poster_url}
                    alt={ev.name}
                    className="w-24 h-24 object-cover rounded-xl border border-white/5 flex-shrink-0"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-[#FF007F]/30 to-[#9D00FF]/30 flex items-center justify-center border border-white/5 flex-shrink-0">
                    <CalendarDays className="w-8 h-8 text-white/40" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-base truncate">{ev.name}</p>
                  <p className="text-[#00FFFF] text-xs font-semibold mt-1 flex items-center gap-1 flex-wrap">
                    <CalendarDays className="w-3.5 h-3.5" /> {formatDate(ev.next_date || ev.event_date)}
                    {!ev.is_all_day && ev.start_time && (
                      <>
                        <Clock className="w-3.5 h-3.5 ml-2" /> {ev.start_time}
                      </>
                    )}
                    {ev.is_all_day && (
                      <span className="ml-2 text-gray-400">All day</span>
                    )}
                  </p>
                  {ev.recurrence && (
                    <p className="text-[#d9a3ff] text-[11px] font-semibold mt-1 flex items-center gap-1" data-testid={`venue-event-recurring-${ev.id}`}>
                      <Repeat className="w-3 h-3" /> {ev.recurrence_label}
                    </p>
                  )}
                  {ev.description && (
                    <p className="text-gray-400 text-xs mt-2 line-clamp-2">{ev.description}</p>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VenueEventsListPage;
